import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ClipboardList, Upload, Loader2, Trash2, X, Images } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtDate, fmtNum } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

const MAX_IMAGES = 20;
const PICK = "image/png,image/jpeg";

/** What the View window shows for each kind of record: [label, key, format]. */
const FIELDS = {
  agreements: [["Title", "title"], ["Type", "agreement_type"], ["Parties", "parties"], ["Start date", "start_date", "date"], ["End date", "end_date", "date"],
    ["Renewal date", "renewal_date", "date"], ["Value", "value", "num"], ["Status", "status"], ["Notes", "notes"]],
  "statutory-approvals": [["Authority", "authority"], ["Approval type", "approval_type"], ["Reference no.", "reference_no"], ["Applied date", "applied_date", "date"],
    ["Valid until", "valid_until", "date"], ["Status", "status"], ["Notes", "notes"]],
  cases: [["Case number", "case_number"], ["Forum", "forum"], ["Party", "party"], ["Filed date", "filed_date", "date"], ["Next hearing", "next_hearing_date", "date"],
    ["Counsel", "counsel"], ["Status", "status"], ["Remarks", "remarks"]],
};
const show = (v, f) => (v === null || v === undefined || v === "" ? "—" : f === "date" ? fmtDate(v) : f === "num" ? fmtNum(v, 2) : String(v).replaceAll("_", " "));

/** Send the images picked in a "New ..." form once the record exists. Never throws: the record is already saved. */
export async function uploadStaged(kind, entityId, files) {
  if (!files?.length) return true;
  const form = new FormData();
  files.forEach((f) => form.append("files", f));
  try {
    await api.post(`/legal/${kind}/${entityId}/images`, form, { headers: { "Content-Type": "multipart/form-data" } });
    return true;
  } catch (err) {
    toast.error(`Saved, but the images were not uploaded: ${getErrorMessage(err)}. Open the record and add them again.`);
    return false;
  }
}

/** Image picker for a "New ..." form: choose photos now, they upload when the record is created. */
export function StagedImages({ files, onChange }) {
  const [previews, setPreviews] = useState([]);
  useEffect(() => {
    const urls = files.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [files]);

  const pick = (e) => {
    const chosen = Array.from(e.target.files || []);
    e.target.value = "";
    const ok = chosen.filter((f) => /\.(png|jpe?g)$/i.test(f.name));
    if (ok.length < chosen.length) toast.error("Only JPG or PNG images can be added");
    const next = [...files, ...ok];
    if (next.length > MAX_IMAGES) toast.error(`At most ${MAX_IMAGES} images`);
    onChange(next.slice(0, MAX_IMAGES));
  };

  return (
    <div data-testid="staged-images">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">Photos &amp; scans <span className="font-normal text-muted-foreground">(optional)</span></span>
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs font-medium hover:bg-muted" data-testid="staged-images-add">
          <Upload className="h-3.5 w-3.5" /> Add images
          <input type="file" accept={PICK} multiple className="hidden" onChange={pick} data-testid="staged-images-input" />
        </label>
      </div>
      {files.length === 0 ? (
        <p className="mt-1.5 text-xs text-muted-foreground">JPG or PNG. They are saved with the record when you click Create.</p>
      ) : (
        <div className="mt-2 grid grid-cols-4 gap-2 sm:grid-cols-5">
          {files.map((f, i) => (
            <div key={`${f.name}-${i}`} className="group relative aspect-square overflow-hidden rounded-md border border-border bg-muted" data-testid={`staged-image-${i}`}>
              {previews[i] && <img src={previews[i]} alt={f.name} className="h-full w-full object-cover" />}
              <button type="button" onClick={() => onChange(files.filter((_, j) => j !== i))} aria-label={`Remove ${f.name}`}
                className="absolute right-1 top-1 rounded-full bg-background/90 p-0.5 shadow hover:bg-background" data-testid={`staged-image-remove-${i}`}><X className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** The photos / scans of a saved record: thumbnails, full-size view, add, delete. */
function Gallery({ kind, entityId }) {
  const [items, setItems] = useState([]);
  const [urls, setUrls] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [big, setBig] = useState(null);
  const [askDelete, setAskDelete] = useState(null);
  const made = useRef([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/legal/${kind}/${entityId}/images`);
      const next = {};
      await Promise.all(data.map(async (img) => {
        try {
          const r = await api.get(`/legal/images/${img.id}`, { responseType: "blob" });
          const u = URL.createObjectURL(r.data);
          made.current.push(u); next[img.id] = u;
        } catch { /* the thumbnail stays blank */ }
      }));
      setItems(data); setUrls((old) => ({ ...old, ...next }));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [kind, entityId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { made.current.forEach((u) => URL.revokeObjectURL(u)); made.current = []; }, []);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      const form = new FormData();
      files.forEach((f) => form.append("files", f));
      await api.post(`/legal/${kind}/${entityId}/images`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(files.length === 1 ? "Image uploaded" : `${files.length} images uploaded`);
      await load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/legal/images/${id}`);
      toast.success("Image deleted");
      setAskDelete(null); setBig((b) => (b === id ? null : b));
      await load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const bigItem = items.find((i) => i.id === big);
  return (
    <section data-testid="legal-gallery">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold"><Images className="h-4 w-4 text-muted-foreground" /> Photos &amp; scans {items.length ? <span className="font-normal text-muted-foreground">({items.length})</span> : null}</h3>
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90" data-testid="images-upload-label">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Add images
          <input type="file" accept={PICK} multiple className="hidden" onChange={upload} disabled={uploading} data-testid="images-upload-input" />
        </label>
      </div>

      {bigItem && (
        <div className="relative mt-3 rounded-lg border border-border bg-muted/30 p-2" data-testid="images-big">
          <button type="button" onClick={() => setBig(null)} aria-label="Close preview" className="absolute right-3 top-3 z-10 rounded-full bg-background/90 p-1 shadow hover:bg-background"><X className="h-4 w-4" /></button>
          {urls[bigItem.id] ? <img src={urls[bigItem.id]} alt={bigItem.filename} className="max-h-[38vh] w-full rounded-md object-contain" /> : <div className="grid h-40 place-items-center text-sm text-muted-foreground">Image not available</div>}
          <p className="mt-1 truncate text-center text-xs text-muted-foreground">{bigItem.filename}</p>
        </div>
      )}

      {loading ? (
        <div className="grid h-20 place-items-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : items.length === 0 ? (
        <div className="mt-3 rounded-lg border border-dashed border-border py-8 text-center text-sm text-muted-foreground" data-testid="images-empty">No images yet. Use &ldquo;Add images&rdquo; to attach photos or scans.</div>
      ) : (
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4" data-testid="images-grid">
          {items.map((img) => (
            <figure key={img.id} className={"overflow-hidden rounded-lg border bg-card " + (big === img.id ? "border-primary ring-1 ring-primary" : "border-border")} data-testid={`image-card-${img.id}`}>
              <button type="button" onClick={() => setBig(img.id)} className="block aspect-[4/3] w-full bg-muted" aria-label={`View ${img.filename}`}>
                {urls[img.id] ? <img src={urls[img.id]} alt={img.filename} className="h-full w-full object-cover" /> : <span className="grid h-full place-items-center text-xs text-muted-foreground">No preview</span>}
              </button>
              <figcaption className="flex items-center justify-between gap-1 px-2 py-1.5">
                <span className="min-w-0 truncate text-xs text-muted-foreground" title={img.filename}>{img.filename}</span>
                {askDelete === img.id ? (
                  <span className="flex shrink-0 items-center gap-1 text-xs">
                    <button type="button" onClick={() => remove(img.id)} className="font-semibold text-destructive hover:underline" data-testid={`image-delete-yes-${img.id}`}>Delete</button>
                    <button type="button" onClick={() => setAskDelete(null)} className="text-muted-foreground hover:underline">No</button>
                  </span>
                ) : (
                  <button type="button" onClick={() => setAskDelete(img.id)} aria-label={`Delete ${img.filename}`} className="shrink-0 text-muted-foreground hover:text-destructive" data-testid={`image-delete-${img.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                )}
              </figcaption>
            </figure>
          ))}
        </div>
      )}
    </section>
  );
}

/** Row button: opens the saved record (every field) with its photos & scans. */
export default function LegalImages({ kind, entity }) {
  const [open, setOpen] = useState(false);
  const name = entity.title || entity.approval_type || entity.case_number || "Record";
  const fields = FIELDS[kind] || [];
  return (
    <>
      <Button size="sm" variant="ghost" onClick={() => setOpen(true)} title="View record and photos" aria-label="View record and photos" data-testid={`images-button-${entity.id}`}>
        <ClipboardList className="h-3.5 w-3.5" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] w-full max-w-3xl overflow-y-auto" data-testid={`images-dialog-${entity.id}`}>
          <DialogHeader><DialogTitle>{name}</DialogTitle></DialogHeader>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2" data-testid="legal-details">
            {fields.map(([label, key, f]) => (
              <div key={key} className={["parties", "notes", "remarks"].includes(key) ? "sm:col-span-2" : ""}>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</dt>
                <dd className="mt-0.5 whitespace-pre-wrap text-sm text-foreground">{show(entity[key], f)}</dd>
              </div>
            ))}
          </dl>
          <div className="border-t border-border pt-4">{open && <Gallery kind={kind} entityId={entity.id} />}</div>
        </DialogContent>
      </Dialog>
    </>
  );
}
